import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { api, formatApiError } from "../lib/api";

const MIN_PASSWORD_LENGTH = 10;

function describeError(code, minLength) {
  switch (code) {
    case "invalid_token":
      return "This reset link isn't valid. Request a new one from the sign-in page.";
    case "token_already_used":
      return "This reset link has already been used. Request a fresh one if you still need to change your password.";
    case "token_expired":
      return "This reset link has expired. Request a new one from the sign-in page.";
    case "password_too_short":
      return `Choose a longer password — at least ${minLength || MIN_PASSWORD_LENGTH} characters.`;
    case "user_not_found":
      return "We couldn't find the account associated with this link. Request a new link from the sign-in page.";
    default:
      return "We couldn't complete the reset. Request a new link and try again.";
  }
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const missingToken = !token;
  const mismatch = password && confirm && password !== confirm;
  const tooShort = password && password.length < MIN_PASSWORD_LENGTH;
  const canSubmit =
    !busy && !missingToken && password && confirm && !mismatch && !tooShort;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setDone(true);
      // Give the user a beat to read the confirmation.
      setTimeout(() => navigate("/brand-os/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code =
        (detail && typeof detail === "object" && detail.code) ||
        (typeof detail === "string" ? detail : "");
      const minLen =
        detail && typeof detail === "object" ? detail.min_length : undefined;
      setError(code ? describeError(code, minLen) : formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bo-root" data-testid="reset-password-screen">
      <div className="bo-login-shell">
        <div className="bo-login-card">
          <div className="bo-login-eyebrow" data-testid="reset-eyebrow">
            Precipice Brand OS · Choose a new password
          </div>
          <h1 className="bo-login-title" data-testid="reset-title">
            Set a new password.
          </h1>
          <p className="bo-login-lede">
            Pick something at least {MIN_PASSWORD_LENGTH} characters long. After
            this, all other active sessions for your account will be signed out.
          </p>

          {missingToken && (
            <div
              className="bo-login-error"
              data-testid="reset-missing-token"
              role="alert"
              style={{ marginBottom: 12 }}
            >
              This page needs a reset link. Open the link from your email, or{" "}
              <Link to="/brand-os/forgot-password" data-testid="reset-request-new">
                request a new one
              </Link>
              .
            </div>
          )}

          {done ? (
            <div className="bo-login-form" data-testid="reset-success">
              <div
                style={{
                  background: "var(--bo-surface-subtle, #f3f0ea)",
                  border: "1px solid var(--bo-border, #e6e2d8)",
                  padding: "14px 16px",
                  borderRadius: 6,
                  color: "var(--bo-ink, #2b2c30)",
                  lineHeight: 1.5,
                }}
                role="status"
              >
                Password updated. Redirecting you to sign in…
              </div>
              <Link
                to="/brand-os/login"
                className="bo-btn bo-btn-primary"
                data-testid="reset-go-to-login"
                style={{ textAlign: "center", textDecoration: "none" }}
              >
                Sign in now
              </Link>
            </div>
          ) : (
            <form
              className="bo-login-form"
              onSubmit={handleSubmit}
              data-testid="reset-form"
            >
              <div className="bo-field">
                <label className="bo-label" htmlFor="bo-reset-password">
                  New password
                </label>
                <input
                  id="bo-reset-password"
                  className="bo-input"
                  data-testid="reset-password-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy || missingToken}
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              <div className="bo-field">
                <label className="bo-label" htmlFor="bo-reset-confirm">
                  Confirm new password
                </label>
                <input
                  id="bo-reset-confirm"
                  className="bo-input"
                  data-testid="reset-confirm-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={busy || missingToken}
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>

              {tooShort && (
                <div
                  className="bo-login-error"
                  data-testid="reset-too-short"
                  role="alert"
                >
                  Choose at least {MIN_PASSWORD_LENGTH} characters.
                </div>
              )}
              {mismatch && !tooShort && (
                <div
                  className="bo-login-error"
                  data-testid="reset-mismatch"
                  role="alert"
                >
                  The two passwords don&rsquo;t match.
                </div>
              )}
              {error && (
                <div
                  className="bo-login-error"
                  data-testid="reset-error"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="bo-btn bo-btn-primary"
                data-testid="reset-submit-button"
                disabled={!canSubmit}
              >
                {busy ? "Updating…" : "Update password"}
              </button>

              <Link
                to="/brand-os/login"
                data-testid="reset-back-link"
                style={{
                  textAlign: "center",
                  fontSize: 13,
                  color: "var(--bo-muted, #6b6f76)",
                  textDecoration: "none",
                }}
              >
                Back to sign in
              </Link>
            </form>
          )}

          <div className="bo-login-footer">
            Reset links are single-use and expire 30 minutes after they&rsquo;re sent.
          </div>
        </div>
      </div>
    </div>
  );
}
